import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loading } from "@/components/common/Loading";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { FeatureStatusIcon, FeatureLabelBadge } from "@/components/pricing/featureBits";
import FoundersSection from "@/components/pricing/FoundersSection";
import { useAuth } from "@/context/AuthContext";

const money = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
};

function isExternal(url) {
  return /^(https?:|mailto:|tel:)/i.test(url || "");
}

function CtaButton({ tier, highlighted }) {
  // Contact/Teams tiers (custom price) link to the Enterprise page unless an admin
  // set a custom CTA URL; everyone else defaults to register.
  const label = tier.cta_label || (tier.custom_price ? "Contact us" : "Get started");
  const url = tier.cta_url || (tier.custom_price ? "/enterprise" : "/register");
  const variant = highlighted ? "default" : "secondary";
  const props = { className: "w-full", variant, "data-testid": `pricing-cta-${tier.tier_key}` };
  if (isExternal(url)) {
    return <a href={url}><Button {...props}>{label}</Button></a>;
  }
  return <Link to={url}><Button {...props}>{label}</Button></Link>;
}

function PriceBlock({ tier, annual }) {
  if (tier.custom_price) {
    return (
      <div className="mt-3" data-testid={`pricing-price-${tier.tier_key}`}>
        <span className="text-3xl font-[760] text-foreground">{tier.custom_price}</span>
      </div>
    );
  }
  const monthly = Number(tier.price_monthly || 0);
  const perMonthAnnual = Number(tier.price_monthly_annual || 0);
  const isFree = monthly === 0 && perMonthAnnual === 0;

  if (isFree) {
    return (
      <div className="mt-3" data-testid={`pricing-price-${tier.tier_key}`}>
        <span className="text-3xl font-[760] text-foreground">Free</span>
        <p className="mt-1 text-xs text-muted-foreground">Forever — no card required.</p>
      </div>
    );
  }

  if (annual) {
    return (
      <div className="mt-3" data-testid={`pricing-price-${tier.tier_key}`}>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-[760] text-foreground">{money(perMonthAnnual)}</span>
          <span className="text-sm text-muted-foreground">/mo</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {money(tier.price_annual)}/yr billed annually
          {tier.savings_pct ? (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-teal-50 px-1.5 py-0.5 text-[11px] font-[640] text-teal-700" data-testid={`pricing-savings-${tier.tier_key}`}>
              Save {tier.savings_pct}%
            </span>
          ) : null}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid={`pricing-price-${tier.tier_key}`}>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-[760] text-foreground">{money(monthly)}</span>
        <span className="text-sm text-muted-foreground">/mo</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Billed monthly</p>
    </div>
  );
}

function TierCard({ tier, annual }) {
  return (
    <Card
      className={`relative flex flex-col p-6 ${tier.highlighted ? "ring-2 ring-primary" : ""}`}
      data-testid={`pricing-card-${tier.tier_key}`}
    >
      {tier.highlighted && (
        <Badge className="absolute right-4 top-4 bg-teal-50 text-teal-700" data-testid={`pricing-popular-${tier.tier_key}`}>Most popular</Badge>
      )}
      <h3 className="text-xl font-[760] text-foreground">{tier.name}</h3>
      {tier.subtitle && <p className="mt-1 text-sm text-muted-foreground">{tier.subtitle}</p>}
      <PriceBlock tier={tier} annual={annual} />
      <div className="mt-5"><CtaButton tier={tier} highlighted={tier.highlighted} /></div>

      {tier.features_heading && (
        <p className="mt-6 text-sm font-[640] text-foreground">{tier.features_heading}</p>
      )}
      <ul className="mt-3 space-y-2.5">
        {(tier.features || []).map((f, i) => (
          <li key={f.id || i} className="flex items-start gap-2 text-sm text-foreground" data-testid={`pricing-feature-${tier.tier_key}-${i}`}>
            <FeatureStatusIcon status={f.status} className="mt-0.5" />
            <span className={f.status === "not_included" ? "text-muted-foreground line-through" : ""}>
              {f.name}<FeatureLabelBadge label={f.label} />
            </span>
          </li>
        ))}
      </ul>

      {tier.footnote && tier.footnote !== "<p></p>" && (
        <div className="mt-5 border-t border-border/60 pt-4 text-xs text-muted-foreground" data-testid={`pricing-footnote-${tier.tier_key}`}>
          <RichTextView html={tier.footnote} />
        </div>
      )}
    </Card>
  );
}

function ComparisonTable({ tiers, rows }) {
  if (!rows || rows.length === 0 || tiers.length === 0) return null;
  return (
    <div className="mt-20" data-testid="pricing-comparison">
      <h2 className="text-center text-2xl font-[720] tracking-[-0.01em] text-foreground">Compare plans</h2>
      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="py-3 pr-4 text-left font-[640] text-muted-foreground">Feature</th>
              {tiers.map((t) => (
                <th key={t.tier_key} className="px-3 py-3 text-center font-[700] text-foreground" data-testid={`pricing-compare-col-${t.tier_key}`}>{t.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60" data-testid={`pricing-compare-row-${r.id}`}>
                <td className="py-3 pr-4 text-left font-[540] text-foreground">
                  {r.name}<FeatureLabelBadge label={r.label} />
                </td>
                {tiers.map((t) => (
                  <td key={t.tier_key} className="px-3 py-3 text-center">
                    <span className="inline-flex justify-center">
                      <FeatureStatusIcon status={(r.values || {})[t.tier_key] || "not_included"} />
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><FeatureStatusIcon status="included" /> Included</span>
        <span className="flex items-center gap-1.5"><FeatureStatusIcon status="addon" /> Add-on</span>
        <span className="flex items-center gap-1.5"><FeatureStatusIcon status="not_included" /> Not included</span>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [annual, setAnnual] = useState(true); // default: annually
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useQuery({ queryKey: ["pricing"], queryFn: async () => (await api.get("/pricing")).data });

  if (isLoading) return <Loading />;
  const tiers = data?.tiers || [];
  const comparison = data?.comparison || [];
  const cols = tiers.length === 1 ? "lg:grid-cols-1" : tiers.length === 2 ? "lg:grid-cols-2" : tiers.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4";

  return (
    <div className="pb-16">
      {/* Exclusive Founders Membership — shown FIRST, above the standard plans */}
      <FoundersSection />

      <div className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-[720] tracking-[-0.02em] text-foreground sm:text-5xl">Plans for every stage</h1>
          <p className="mt-3 text-muted-foreground">Start free. Upgrade when you&apos;re ready to unlock meetings, pitch decks, and analytics.</p>
        </div>

        {/* Billing toggle */}
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex items-center rounded-full border border-border bg-secondary/50 p-1" data-testid="pricing-billing-toggle">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-4 py-1.5 text-sm font-[600] transition-colors ${!annual ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="pricing-billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-[600] transition-colors ${annual ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="pricing-billing-annual"
            >
              Annually
              <span className="inline-flex items-center rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-[700] text-teal-700">Save</span>
            </button>
          </div>
        </div>

        <div className={`mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 ${cols}`}>
          {tiers.map((t) => <TierCard key={t.id} tier={t} annual={annual} />)}
        </div>

        {/* Comparison matrix — temporarily hidden from the public; visible to admins only */}
        {isAdmin && (
          <>
            <p className="mt-16 text-center text-xs font-[600] uppercase tracking-wide text-amber-600" data-testid="pricing-comparison-admin-note">
              Admin preview — this comparison table is hidden from the public
            </p>
            <ComparisonTable tiers={tiers} rows={comparison} />
          </>
        )}
      </div>

      <p className="mx-auto mt-12 max-w-7xl px-4 text-center text-sm text-muted-foreground">
        Tiers and what they unlock are managed by Startup Otter and can change over time.
      </p>
    </div>
  );
}
